import { motion } from "framer-motion";
import { ArrowRight, Clock, ShieldCheck, Zap } from "lucide-react";

const HERO_BG =
  "https://images.pexels.com/photos/31363974/pexels-photo-31363974.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1024&w=1536";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden pt-32 pb-16 md:pb-24"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden
      />
      {/* Heavy navy overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/85 via-[#050B14]/80 to-[#050B14]" aria-hidden />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.12),transparent_60%)]" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-semibold text-gold border border-gold/30 px-4 py-2 mb-8"
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse-gold" />
              Same-Day Funding Available
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-white leading-[1.02]"
              data-testid="hero-headline"
            >
              Get Funded in <span className="text-gold font-medium">24&nbsp;Hours</span>
              <br />
              <span className="text-white/80">— Without the Bank Runaround.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              DirectFundCap pairs in-house capital with a vetted lender network so you get
              decisions in hours and money in your account the same day. No paperwork
              marathons. No hidden fees. Just capital — fast.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href="#apply"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-400 text-navy-950 font-semibold px-8 py-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_28px_rgba(212,175,55,0.45)]"
              >
                Get Approved Now
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#products"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-3 border border-white/20 hover:border-white/60 text-white px-8 py-4 transition-all duration-300 hover:bg-white/5"
              >
                See Your Options
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-14 grid grid-cols-3 gap-px border border-white/10 bg-white/5 backdrop-blur max-w-2xl"
              data-testid="hero-trust-bar"
            >
              {[
                { icon: Clock, label: "24-Hour Funding", sub: "Same-day options" },
                { icon: ShieldCheck, label: "No Hidden Fees", sub: "Transparent terms" },
                { icon: Zap, label: "Soft Pull Only", sub: "Won't hurt credit" },
              ].map((s, i) => (
                <div key={i} className="bg-[#050B14] p-5 flex flex-col gap-2">
                  <s.icon className="w-5 h-5 text-gold" />
                  <div className="text-white text-sm font-semibold">{s.label}</div>
                  <div className="text-white/50 text-xs">{s.sub}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Floating card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block lg:col-span-4"
          >
            <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-gold">Live Funding Today</div>
              <div className="mt-6 font-heading text-5xl font-light text-white tracking-tighter">
                $1.4M<span className="text-gold">+</span>
              </div>
              <div className="text-white/60 text-sm mt-1">Deployed to small businesses · last 24h</div>
              <div className="mt-8 space-y-3 text-sm">
                {[
                  ["Restaurant · NY", "$120K", "8m ago"],
                  ["Construction · TX", "$285K", "23m ago"],
                  ["Trucking · FL", "$75K", "1h ago"],
                ].map(([biz, amt, t]) => (
                  <div key={biz} className="flex items-center justify-between border-b border-white/5 pb-3">
                    <div>
                      <div className="text-white/90">{biz}</div>
                      <div className="text-white/40 text-xs">{t}</div>
                    </div>
                    <div className="text-gold font-semibold">{amt}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-2 text-xs text-white/50">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Funding desk open · Mon–Sat
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
